/**
 * ראשי — REDESIGN §4 (personal), which replaces spec-personal §3.1.
 *
 * One reading order, top to bottom, on desktop and on mobile:
 *   1. `מה קורה`   — the written summary, plain ground, above every figure (§3, §4.1)
 *   2. the ONE hero figure `נשאר אחרי החיובים` with its two inputs and the coverage chip (§4.2)
 *   3. `עכשיו`      — the urgent list, one shared row component (§4.3)
 *   4. `השבוע` / `החודש` — the same rows, collapsed to 3 each (§4.4)
 *   5. `אפשר לחכות` — one collapsed line that opens in place (§4.5)
 *   6. the quiet detail strip — a heading, ≤5 lines and a link per block (§4.6)
 *
 * Gone from this screen (§4, "Removed from home"): the four-tile row, the coverage footnote
 * paragraph, the 7-bar category list, the accounts list and the 5-row recent-transactions list.
 * Their content lives on their own screens; the strip links to each.
 */

import type { SummaryResponse } from '../../../shared-types.ts';
import { fmtDM, fmtMoney, fmtPct } from '../format.ts';
import { periodLabel } from '../dates.ts';
import { CATEGORY, he } from '../i18n/he.ts';
import { URGENCY_BUCKET_LABELS } from '../insight.ts';
import { Icon } from '../components/Icon.tsx';
import { CategoryIcon, Chip, EmptyState, Money } from '../components/ui.tsx';
import { LaterSection, NarrativeBlock, StripBlock, UrgencySection } from '../components/insight.tsx';
import { DivergingBars } from '../charts/DivergingBars.tsx';
import { Sparkline } from '../charts/Sparkline.tsx';
import { Link } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';

/** §4.4 — `השבוע` and `החודש` show their first three rows. */
const COLLAPSE_AT = 3;

export function HomeScreen({ summary }: { summary: SummaryResponse }) {
  if (summary.state !== 'ok') {
    return (
      <div className="screen">
        <EmptyState
          illustration="firstRun"
          title={he.empty.firstRun.title}
          body={he.empty.firstRun.body}
          action={
            <Link className="btn primary" to={buildPath('settings', 'connections')}>
              {he.refresh.toConnections}
            </Link>
          }
        />
      </div>
    );
  }

  const buckets = summary.urgency.groups.filter((group) => group.bucket !== 'later');
  const later = summary.urgency.groups.find((group) => group.bucket === 'later');

  return (
    <div className="screen home">
      <NarrativeBlock narrative={summary.narrative} />
      <Hero summary={summary} />

      {buckets.length ? (
        buckets.map((group) => (
          <UrgencySection
            key={group.bucket}
            bucket={group.bucket}
            label={URGENCY_BUCKET_LABELS[group.bucket]}
            items={group.items}
            limit={group.bucket === 'now' ? undefined : COLLAPSE_AT}
          />
        ))
      ) : (
        <section className="fk-section urgency">
          <h2 className="fk-section-title">{URGENCY_BUCKET_LABELS.now}</h2>
          <EmptyState variant="inline" illustration="noAnomalies" title={he.now.allClearTitle} body={he.now.allClearBody} />
        </section>
      )}

      <LaterSection items={later?.items ?? []} />
      <DetailStrip summary={summary} />
    </div>
  );
}

// ───────────────────────────── §4.2 the one number ─────────────────────────────

/**
 * The only figure on this screen at hero size (§8 "one hero per screen"). Its two inputs sit
 * beneath it in one small line and the coverage state is a chip — the footnote paragraph the
 * old screen carried became one narrative sentence instead (§4, "Removed from home").
 */
function Hero({ summary }: { summary: SummaryResponse }) {
  const overall = summary.coverage.overall;

  if (overall === null) {
    const checking = summary.accounts.find((row) => row.account.type === 'CHECKING');
    return (
      <section className="hero" aria-label={he.home.availableNoMask}>
        <span className="fk-meta">{he.home.availableNoMask}</span>
        <p className="fk-hero">
          <Money agorot={checking?.displayBalanceAgorot ?? 0} />
        </p>
        <p className="fk-hero-inputs">
          <Chip tone="muted" icon="info">
            {he.now.coverageUnknown}
          </Chip>
        </p>
      </section>
    );
  }

  const ok = overall.coverageKnownAgorot >= 0;
  return (
    <section className="hero" aria-label={he.now.heroLabel}>
      <span className="fk-meta">{he.now.heroLabel}</span>
      <p className={`fk-hero${ok ? '' : ' critical'}`}>
        <Money agorot={overall.coverageKnownAgorot} />
      </p>
      <p className="fk-hero-inputs">
        <span>{he.now.heroInputs(fmtMoney(overall.availableAgorot), fmtMoney(overall.chargesKnownAgorot))}</span>{' '}
        <Chip tone={ok ? 'good' : 'critical'} icon={ok ? 'checkCircle' : 'alert'}>
          {ok ? he.now.covered : he.now.missing(fmtMoney(Math.abs(overall.coverageKnownAgorot)))}
        </Chip>
      </p>
    </section>
  );
}

// ───────────────────────────── §4.6 the quiet detail strip ─────────────────────────────

function DetailStrip({ summary }: { summary: SummaryResponse }) {
  return (
    <div className="fk-strip strip">
      <FlowBlock summary={summary} />
      <CategoriesBlock summary={summary} />
      <CardsBlock summary={summary} />
      <LoansBlock summary={summary} />
      <NetWorthBlock summary={summary} />
      <AccountsBlock summary={summary} />
    </div>
  );
}

function FlowBlock({ summary }: { summary: SummaryResponse }) {
  const { cashflow, period } = summary;
  return (
    <StripBlock title={he.strip.flow} href={buildPath('transactions')} linkLabel={he.strip.flowLink}>
      <DivergingBars
        question={he.home.flowTitle(periodLabel(period))}
        summary={`${he.home.net} ${fmtMoney(cashflow.bank.netAgorot, 0, true)}`}
        rows={[
          { label: he.home.in, value: cashflow.bank.inAgorot },
          { label: he.home.out, value: -cashflow.bank.outAgorot },
        ]}
        table={{
          head: [he.strip.flowTblDir, he.strip.flowTblAmount],
          rows: [
            [he.home.in, fmtMoney(cashflow.bank.inAgorot)],
            [he.home.out, fmtMoney(cashflow.bank.outAgorot)],
            [he.home.net, fmtMoney(cashflow.bank.netAgorot, 0, true)],
          ],
        }}
      />
    </StripBlock>
  );
}

/** §4.6 — "קטגוריות (top 3 deviations only)". */
function CategoriesBlock({ summary }: { summary: SummaryResponse }) {
  const over = summary.categories.filter((stat) => stat.over).slice(0, 3);
  return (
    <StripBlock title={he.strip.cats} href={buildPath('categories')} linkLabel={he.strip.catsLink}>
      {over.length ? (
        <ul className="striplist">
          {over.map((stat) => (
            <li key={stat.groupKey}>
              <CategoryIcon groupKey={stat.groupKey} small />
              <Link className="grow" to={buildPath('categories', stat.groupKey)}>
                {CATEGORY[stat.groupKey]?.label ?? stat.groupKey}
              </Link>
              <span className="num tab">{fmtMoney(stat.monthAmountAgorot)}</span>
              {stat.ratio === null ? null : <Chip tone="warn">{he.home.over(stat.ratio.toFixed(1))}</Chip>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="fk-meta">{he.strip.catsNone}</p>
      )}
    </StripBlock>
  );
}

function CardsBlock({ summary }: { summary: SummaryResponse }) {
  // `כרטיסים פעילים` means ENABLED, not "every card that is not deleted" — blocked cards were
  // being counted, so the line read 4 where the live data has 2 (BRIEF §9.2, ADDENDUM).
  const cards = summary.accounts.filter((row) => row.account.type === 'CARD' && row.account.creditStatus === 'enabled');
  const used = cards.reduce((sum, row) => sum + Math.abs(row.displayBalanceAgorot ?? 0), 0);
  const next = summary.coverage.items[0];
  return (
    <StripBlock title={he.strip.cards} href={buildPath('cards')} linkLabel={he.strip.cardsLink}>
      <p className="fk-body">{he.strip.cardsLine(cards.length, fmtMoney(used))}</p>
      {next ? (
        <p className="fk-meta">
          {next.name} · <span className="num">{fmtDM(next.dueDate)}</span> ·{' '}
          <span className="num">{fmtMoney(next.knownAgorot)}</span>
        </p>
      ) : (
        <p className="fk-meta">{he.home.noChargesWindow}</p>
      )}
    </StripBlock>
  );
}

/**
 * §6 — "Also a 4-line summary block in the home detail strip", capped at 5 lines by §4.6.
 *
 * Three label/value pairs and ONE sentence. The trend line is gone: the loans screen states it
 * under its own chart, while the ending-soon sentence is the only fact that screen's header does
 * not repeat. Dropping it also puts this block back at its row-mates' height.
 */
function LoansBlock({ summary }: { summary: SummaryResponse }) {
  const { totals, endingSoon } = summary.loans;
  return (
    <StripBlock title={he.strip.loans} href={buildPath('loans')} linkLabel={he.strip.loansLink}>
      {totals.openCount === 0 ? (
        <p className="fk-meta">{he.loans.empty}</p>
      ) : (
        <ul className="striplist plain">
          <li>
            <span className="grow">{he.loans.debtTotal}</span>
            <span className="num tab">{fmtMoney(totals.debtAgorot)}</span>
          </li>
          <li>
            <span className="grow">{he.loans.monthly}</span>
            <span className="num tab">{fmtMoney(totals.monthlyPaymentAgorot)}</span>
          </li>
          <li>
            <span className="grow">{he.loans.weightedRate}</span>
            <span className="num tab">
              {totals.weightedRatePct === null ? he.app.unavailable : he.loans.ratePct(totals.weightedRatePct.toFixed(2))}
            </span>
          </li>
        </ul>
      )}
      {endingSoon.count > 0 && endingSoon.withinMonths !== null ? (
        <p className="fk-meta">
          {endingSoon.count === 1
            ? he.loans.timelineFreeOne(endingSoon.withinMonths, fmtMoney(endingSoon.monthlyFreedAgorot))
            : he.loans.timelineFree(endingSoon.withinMonths, endingSoon.count, fmtMoney(endingSoon.monthlyFreedAgorot))}
        </p>
      ) : null}
    </StripBlock>
  );
}

function NetWorthBlock({ summary }: { summary: SummaryResponse }) {
  const nw = summary.netWorth;
  const up = nw.monthChangeAgorot >= 0;
  const first = nw.sparkline[0];
  return (
    <StripBlock title={he.strip.nw} href={buildPath('networth')} linkLabel={he.strip.nwLink}>
      <p className="fk-figure__value">
        <Money agorot={nw.netAgorot} />
      </p>
      <p className={`fk-meta ${up ? 'good' : 'critical'}`}>
        <Icon name={up ? 'upRight' : 'downLeft'} className="xs" />
        {he.home.nwMonth(fmtMoney(nw.monthChangeAgorot, 0, true), fmtPct(nw.monthChangePct))}
      </p>
      <Sparkline
        values={nw.sparkline.map((point) => point.netAgorot)}
        label={he.home.nwSparkAria(fmtMoney(first?.netAgorot ?? 0), fmtMoney(nw.netAgorot))}
      />
    </StripBlock>
  );
}

function AccountsBlock({ summary }: { summary: SummaryResponse }) {
  const rows = summary.accounts.filter((row) => row.account.type === 'CHECKING' || row.account.type === 'SAVINGS');
  const total = rows.reduce((sum, row) => sum + (row.displayBalanceAgorot ?? 0), 0);
  return (
    <StripBlock title={he.strip.accounts} href={buildPath('networth')} linkLabel={he.strip.accountsLink}>
      <p className="fk-body">{he.strip.accountsLine(rows.length, fmtMoney(total))}</p>
      <p className="fk-meta">
        {he.home.accountsFoot} · <Link to={buildPath('settings', 'accounts')}>{he.home.assignAccounts}</Link>
      </p>
    </StripBlock>
  );
}
